import { useEffect, useState } from "react";

import { anfrage, ApiFehler } from "../../api/client";
import type { PortalStatusStufe } from "../../api/typen";
import { Spinner } from "../../komponenten/Basis";
import { MarkenKopf, RechtsLinks } from "../../komponenten/MarkenKopf";
import { AppHinweis } from "../../komponenten/portal/AppHinweis";
import { zeitpunkt } from "../../lib/format";
import { quadratischesIcon, seitenMarkeSetzen } from "../../lib/markenIcon";
import { statusManifestSetzen, tokenErmitteln, tokenVergessen } from "./linkSpeicher";

/** Antwort von POST /api/status/pruefen – nur die wenigen Felder, die die öffentliche Seite zeigt. */
interface PublicStatus {
  kunde_vorname: string;
  ort: string;
  betrieb_name: string;
  status: PortalStatusStufe;
  status_text: string;
  aktualisiert_am: string;
  betrieb_logo: string | null;
  nur_betrieb: boolean;
  impressum_url: string | null;
  datenschutz_url: string | null;
}

const STUFEN: PortalStatusStufe[] = ["entwurf", "eingegangen", "in_bearbeitung", "beim_netzbetreiber", "fertig"];

function Fortschritt({ status }: { status: PortalStatusStufe }) {
  const aktuell = STUFEN.indexOf(status);
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {STUFEN.map((stufe, i) => (
        <span
          key={stufe}
          className={`h-1.5 flex-1 rounded-full ${i <= aktuell ? "bg-marke" : "bg-neutral-200"}`}
        />
      ))}
    </div>
  );
}

function Fehlermeldung({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-ampel-rot/30 bg-ampel-rotHell px-4 py-3 text-sm text-ampel-rot">
      {text}
      <p className="mt-1 text-ampel-rot/80">Bitte wenden Sie sich in diesem Fall an Ihren Installateur.</p>
    </div>
  );
}

export function StatusSeite() {
  const [token] = useState(tokenErmitteln);
  const [daten, setDaten] = useState<PublicStatus | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [marke, setMarke] = useState<{ name: string; icon: string } | null>(null);

  // Der Token bleibt bewusst in der Adresse (hinter „#“, geht nie an einen Server): Der Link muss nach
  // Neuladen, als Lesezeichen und als Home-Bildschirm-Icon dauerhaft funktionieren.

  useEffect(() => {
    if (!token) {
      setFehler("Dieser Link ist unvollständig. Bitte öffnen Sie den Link aus der Nachricht Ihres Installateurs noch einmal.");
      return;
    }
    anfrage<PublicStatus>("/api/status/pruefen", { methode: "POST", body: { token } })
      .then(setDaten)
      .catch((e: unknown) => {
        if (e instanceof ApiFehler && e.status === 404) {
          tokenVergessen();
          setFehler("Dieser Link ist nicht mehr gültig.");
        } else {
          setFehler("Der Status konnte gerade nicht geladen werden. Bitte versuchen Sie es in ein paar Minuten erneut.");
        }
      });
  }, [token]);

  useEffect(() => {
    if (!daten || !token) return;
    return statusManifestSetzen(token, marke);
  }, [daten, token, marke]);

  // White Label: Tab-Titel, Favicon und App-Icon mit Logo und Namen des Betriebs.
  useEffect(() => {
    if (!daten?.nur_betrieb || !daten.betrieb_name) return;
    let aufraeumen = () => {};
    let aktiv = true;
    const name = daten.betrieb_name;
    void (daten.betrieb_logo ? quadratischesIcon(daten.betrieb_logo) : Promise.resolve(null)).then((icon) => {
      if (!aktiv) return;
      aufraeumen = seitenMarkeSetzen(`Ihre Anmeldung – ${name}`, icon);
      if (icon) setMarke({ name, icon });
    });
    return () => {
      aktiv = false;
      aufraeumen();
    };
  }, [daten]);

  let inhalt;
  if (fehler) {
    inhalt = <Fehlermeldung text={fehler} />;
  } else if (!daten) {
    inhalt = (
      <div className="flex justify-center py-6">
        <Spinner label="Status wird geladen…" />
      </div>
    );
  } else {
    inhalt = (
      <div className="space-y-6">
        <p className="text-sm text-neutral-600">
          Hallo {daten.kunde_vorname}, hier ist der aktuelle Stand Ihrer Solaranlage in <strong>{daten.ort}</strong>
          {daten.nur_betrieb ? "." : <>, im Auftrag von <strong>{daten.betrieb_name}</strong>.</>}
        </p>
        <div className="space-y-3">
          <Fortschritt status={daten.status} />
          <p className="text-lg font-semibold">{daten.status_text}</p>
        </div>
        <p className="text-xs text-neutral-400">Stand: {zeitpunkt(daten.aktualisiert_am)}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 px-4 py-10">
      <div className="w-full max-w-sm space-y-4">
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-karte">
          <MarkenKopf bereit={Boolean(daten)} nurBetrieb={daten?.nur_betrieb ?? false} logo={daten?.betrieb_logo} name={daten?.betrieb_name} />
          <h1 className="mt-6 text-xl font-semibold">Ihre Anmeldung</h1>
          <div className="mt-4">{inhalt}</div>
        </div>
        {daten && (!daten.nur_betrieb || marke) && <AppHinweis icon={marke?.icon} />}
        <RechtsLinks bereit={Boolean(daten)} nurBetrieb={daten?.nur_betrieb ?? false} impressum={daten?.impressum_url} datenschutz={daten?.datenschutz_url} />
      </div>
    </div>
  );
}
